import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getUnitMeasures } from '../../services/Other/GeneralQueries';
import { getSupplyById, updateSupply } from '../../services/Supplies/Supplies';

const sanitizeDecimal = (value, maxDecimals) => {
  const v = String(value ?? '')
    .replace(/[^\d.]/g, '')
    .replace(/(\..*)\./g, '$1');
  const [i, d] = v.split('.');
  if (d === undefined) return i;
  return `${i}.${d.slice(0, maxDecimals)}`;
};

const formatThousands = (raw) => {
  const v = String(raw ?? '').replace(/,/g, '');
  if (!v) return '';
  const [i, d] = v.split('.');
  const intFmt = new Intl.NumberFormat('es-MX').format(Number(i || 0));
  if (d === undefined) return intFmt;
  return `${intFmt}.${d}`;
};

const toNumOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

function EditSuppliesModal({ show, supplyId, onHide, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unitMeasures, setUnitMeasures] = useState([]);
  const [focusedMoney, setFocusedMoney] = useState(null);

  const [formData, setFormData] = useState({
    code: '',
    product_name: '',
    lot: '',
    pieces_per_lot: '',
    lot_cost: '',
    unitofmeasure_id: '',
    capacity_dimensions: '',
    supplier: '',
    unit_cost: '',
    price_per_ml: '',
    isActive: true,
  });

  const requiredMissing = useMemo(() => {
    const req = ['code', 'product_name', 'lot', 'pieces_per_lot', 'lot_cost', 'unitofmeasure_id', 'unit_cost'];
    return req.some((k) => String(formData[k] ?? '').trim() === '');
  }, [formData]);

  // Cargar registro + unidades al abrir
  useEffect(() => {
    if (!show || !supplyId) return;

    (async () => {
      setLoading(true);
      try {
        const units = await getUnitMeasures();

        setUnitMeasures(units ?? []);

        const supplyRes = await getSupplyById(supplyId);
        if (!supplyRes?.result) {
          toast.error(supplyRes?.message ?? 'No se pudo cargar el consumible.');
          onHide?.();
          return;
        }

        const supply = supplyRes.data;

        setFormData({
          code: supply.code ?? '',
          product_name: supply.product_name ?? '',
          lot: supply.lot ?? '',
          pieces_per_lot: supply.pieces_per_lot ?? '',
          lot_cost: supply.lot_cost ?? '',
          unitofmeasure_id: supply.unitofmeasure_id ? String(supply.unitofmeasure_id) : '',
          capacity_dimensions: supply.capacity_dimensions ?? '',
          supplier: supply.supplier ?? '',
          unit_cost: supply.unit_cost ?? '',
          price_per_ml: supply.price_per_ml ?? '',
          isActive: supply.isActive ?? true,
        });
      } catch (e) {
        toast.error('Error cargando datos del consumible.');
        onHide?.();
      } finally {
        setLoading(false);
      }
    })();
  }, [show, supplyId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'code') {
      setFormData((p) => ({ ...p, code: value.toUpperCase() }));
      return;
    }

    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleMoneyChange = (name, value, maxDecimals) => {
    const clean = sanitizeDecimal(value, maxDecimals);
    setFormData((p) => ({ ...p, [name]: clean }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supplyId) return;

    if (requiredMissing) {
      toast.error('Completa los campos requeridos.');
      return;
    }

    const payload = {
      code: String(formData.code || '').toUpperCase().trim(),
      product_name: String(formData.product_name || '').trim(),
      lot: toNumOrNull(formData.lot),
      pieces_per_lot: toNumOrNull(formData.pieces_per_lot),
      lot_cost: toNumOrNull(formData.lot_cost),
      unitofmeasure_id: Number(formData.unitofmeasure_id),
      capacity_dimensions: String(formData.capacity_dimensions || '').trim() || null,
      supplier: String(formData.supplier || '').trim() || null,
      unit_cost: toNumOrNull(formData.unit_cost),
      price_per_ml: toNumOrNull(formData.price_per_ml),
      isActive: !!formData.isActive,
    };

    setSaving(true);
    try {
      const res = await updateSupply(supplyId, payload);

      if (!res?.result) {
        toast.error(res?.message ?? 'No se pudo actualizar.');
        return;
      }

      toast.success('Consumible actualizado.');
      onSaved?.();
    } catch {
      toast.error('Error actualizando consumible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onHide} centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Editar consumible</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className="py-4 text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              Cargando...
            </div>
          ) : (
            <Row className="g-3">
              <Col md={4}>
                <Form.Label>Clave *</Form.Label>
                <Form.Control
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  disabled={saving}
                  style={{ textTransform: 'uppercase' }}
                />
              </Col>

              <Col md={8}>
                <Form.Label>Nombre *</Form.Label>
                <Form.Control
                  name="product_name"
                  value={formData.product_name}
                  onChange={handleInputChange}
                  disabled={saving}
                />
              </Col>

              <Col md={4}>
                <Form.Label>Lote *</Form.Label>
                <Form.Control
                  name="lot"
                  type="number"
                  step="0.001"
                  value={formData.lot}
                  onChange={handleInputChange}
                  disabled={saving}
                />
              </Col>

              <Col md={4}>
                <Form.Label>Piezas por lote *</Form.Label>
                <Form.Control
                  name="pieces_per_lot"
                  type="number"
                  step="0.001"
                  value={formData.pieces_per_lot}
                  onChange={handleInputChange}
                  disabled={saving}
                />
              </Col>

              <Col md={4}>
                <Form.Label>Costo por lote *</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    inputMode="decimal"
                    value={focusedMoney === 'lot_cost' ? formData.lot_cost : formatThousands(formData.lot_cost)}
                    onFocus={() => setFocusedMoney('lot_cost')}
                    onBlur={() => setFocusedMoney(null)}
                    onChange={(e) => handleMoneyChange('lot_cost', e.target.value, 2)}
                    disabled={saving}
                  />
                </InputGroup>
              </Col>

              <Col md={4}>
                <Form.Label>Unidad de Medida *</Form.Label>
                <Form.Select
                  name="unitofmeasure_id"
                  value={formData.unitofmeasure_id}
                  onChange={handleInputChange}
                  disabled={saving}
                >
                  <option value="">Selecciona…</option>
                  {unitMeasures.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.abbreviation})
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={4}>
                <Form.Label>Capacidad de dimensiones</Form.Label>
                <Form.Control
                  name="capacity_dimensions"
                  value={formData.capacity_dimensions}
                  onChange={handleInputChange}
                  disabled={saving}
                />
              </Col>

              <Col md={4}>
                <Form.Label>Proveedor</Form.Label>
                <Form.Control
                  name="supplier"
                  value={formData.supplier}
                  onChange={handleInputChange}
                  disabled={saving}
                />
              </Col>

              <Col md={6}>
                <Form.Label>Costo por unidad *</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    inputMode="decimal"
                    value={focusedMoney === 'unit_cost' ? formData.unit_cost : formatThousands(formData.unit_cost)}
                    onFocus={() => setFocusedMoney('unit_cost')}
                    onBlur={() => setFocusedMoney(null)}
                    onChange={(e) => handleMoneyChange('unit_cost', e.target.value, 2)}
                    disabled={saving}
                  />
                </InputGroup>
              </Col>

              <Col md={6}>
                <Form.Label>Precio por ml</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    inputMode="decimal"
                    value={focusedMoney === 'price_per_ml' ? formData.price_per_ml : formatThousands(formData.price_per_ml)}
                    onFocus={() => setFocusedMoney('price_per_ml')}
                    onBlur={() => setFocusedMoney(null)}
                    onChange={(e) => handleMoneyChange('price_per_ml', e.target.value, 4)}
                    disabled={saving}
                  />
                </InputGroup>
              </Col>
            </Row>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={onHide} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={saving || loading || requiredMissing}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Guardando...
              </>
            ) : (
              'Guardar'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default EditSuppliesModal;